import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Trash } from '@phosphor-icons/react';
import { ConfirmDialog } from '@/components/admin/ConfirmDialog';
import { InviteModal } from './InviteModal';
import { SAWaitlistSkeleton } from './SASkeletons';

const monoFont = "'IBM Plex Mono', monospace";

interface WaitlistEntry {
  id: string; email: string; created_at: string; source: string | null; notes: string | null;
}

export function SAWaitlist() {
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<WaitlistEntry | null>(null);
  const [inviteEmail, setInviteEmail] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase.from('waitlist').select('*').order('created_at', { ascending: true });
    setEntries((data as any[]) || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await supabase.from('waitlist').delete().eq('id', deleteTarget.id);
    setDeleteTarget(null); load();
  };

  // Source distribution
  const sourceDist: Record<string, number> = {};
  let noSource = 0;
  entries.forEach(e => { if (e.source) { sourceDist[e.source] = (sourceDist[e.source] || 0) + 1; } else { noSource++; } });

  if (loading) return <SAWaitlistSkeleton />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ fontFamily: monoFont, fontSize: 18, fontWeight: 300, color: '#F7F6F2' }}>Lista de espera</div>
        <span style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: '#0057FF', backgroundColor: 'rgba(96,165,250,0.1)', padding: '4px 10px' }}>
          {entries.length} inscritos
        </span>
      </div>

      {entries.length === 0 ? (
        <div style={{ fontFamily: monoFont, fontSize: 13, color: 'rgba(247,246,242,0.3)', textAlign: 'center', padding: '40px 0' }}>Nenhum inscrito</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {entries.map(e => (
            <div key={e.id} style={{ backgroundColor: 'rgba(247,246,242,0.04)', border: '0.5px solid rgba(247,246,242,0.12)', padding: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: monoFont, fontSize: 13, fontWeight: 300, color: 'rgba(247,246,242,0.8)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {e.email}
                </div>
                <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.35)', marginTop: 4, display: 'flex', gap: 8, alignItems: 'center' }}>
                  {new Date(e.created_at).toLocaleDateString('pt-BR')}
                  {e.source ? (
                    <span style={{ padding: '1px 6px', border: '0.5px solid rgba(247,246,242,0.15)', fontSize: 12, textTransform: 'uppercase' }}>{e.source}</span>
                  ) : (
                    <span style={{ fontSize: 12, color: 'rgba(247,246,242,0.2)' }}>sem origem</span>
                  )}
                </div>
                {e.notes && (
                  <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.3)', marginTop: 4, fontStyle: 'italic' }}>{e.notes}</div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button
                  onClick={() => setInviteEmail(e.email)}
                  style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', padding: '5px 10px', border: '0.5px solid #00E87A', backgroundColor: 'rgba(0,232,122,0.06)', color: '#00E87A', cursor: 'pointer' }}
                >Convidar</button>
                <button
                  onClick={() => setDeleteTarget(e)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(247,246,242,0.3)', padding: 4, transition: 'color 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#FF3F2D'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(247,246,242,0.3)'}
                ><Trash size={14} weight="thin" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Insight card */}
      {entries.length > 0 && (
        <div style={{ backgroundColor: 'rgba(247,246,242,0.04)', border: '0.5px solid rgba(247,246,242,0.12)', padding: 16, marginTop: 20 }}>
          <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgba(247,246,242,0.4)', marginBottom: 8 }}>INSIGHT</div>
          {noSource > 0 ? (
            <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.5)', lineHeight: 1.6 }}>
              {noSource} inscrito{noSource > 1 ? 's' : ''} sem origem rastreada. Adicionar campo de origem no formulário pode ajudar a entender seus melhores canais.
            </div>
          ) : (
            <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.5)', lineHeight: 1.6 }}>
              {Object.entries(sourceDist).map(([s, c]) => `${c} do ${s}`).join(' · ')}
            </div>
          )}
        </div>
      )}

      <ConfirmDialog open={!!deleteTarget} onOpenChange={o => { if (!o) setDeleteTarget(null); }} title="Remover da lista" description={`Remover ${deleteTarget?.email}?`} confirmLabel="Remover" cancelLabel="Cancelar" onConfirm={handleDelete} destructive />
      {inviteEmail && <InviteModal onClose={() => setInviteEmail(null)} onInviteSent={() => { setInviteEmail(null); load(); }} />}
    </div>
  );
}
